import {
  gtinMiddle12,
  isRetailCodeScan,
  looksLikeProductScan,
  retailCodeGtin14,
  scanGtinMiddle12,
  type ProductScanLike
} from '@/lib/scanning/retailCode';
import {
  getUniqueBySerialCaseInsensitive,
  matchUniqueSerialCaseInsensitive
} from '@/lib/scanning/serialMatch';

/**
 * Zone Count's rules, without React or a camera: which record a scan names, what has been counted,
 * how the count compares with the records, what the review sends, and the draft that survives a
 * reload. Nothing is written while counting — the count lives here, on the handheld — so leaving
 * mid-count changes no inventory.
 */

// ───────────────────────────── the sheet (zoneCountSheet) ─────────────────────────────

export type ZoneCountUnit = {
  serializedItemId: string;
  serialNumber: string;
  gtin?: string | null;
};

export type ZoneCountLot = {
  key: string;
  zoneInventoryBalanceId?: string | null;
  lot?: string | null;
  /** ISO instant, as the server stores it. */
  expiration?: string | null;
  /** units.length + bulkQuantity. */
  expected: number;
  /** Counted by entering a number. */
  bulkQuantity: number;
  /** Counted by scanning each one. */
  units: ZoneCountUnit[];
  quarantinedQuantity: number;
  pickedQuantity: number;
  /** The records disagree: counted like the rest, but nothing on it can be adjusted. */
  drift: boolean;
};

export type ZoneCountProductInfo = {
  key: string;
  fdaProductId?: string | null;
  upcProductId?: string | null;
  productName: string;
  ndc?: string | null;
  tenDigitNdc?: string | null;
  upc?: string | null;
  deaSchedule?: string | null;
  /** SERIALIZED | BULK | UPC */
  kind: string;
  controlled: boolean;
  matchKeys: string[];
};

export type ZoneCountProduct = ZoneCountProductInfo & {
  expected: number;
  lots: ZoneCountLot[];
};

export type ZoneCountContainer = {
  serializedItemId: string;
  serialNumber: string;
  gtin?: string | null;
  /** SSCC | CA | PK */
  type: string;
  unitIds: string[];
};

export type ZoneCountZone = {
  id: string;
  name: string;
  zoneType?: string | null;
  locationId?: string | null;
};

export type ZoneCountSheet = {
  zone: ZoneCountZone;
  locationId: string;
  products: ZoneCountProduct[];
  containers: ZoneCountContainer[];
  expectedTotal: number;
  notes: string[];
  readAt?: string | null;
};

/** zoneCountUnitLookup: what the records know about a serial the sheet does not list. */
export type ZoneCountUnitLookup = {
  serialNumber: string;
  gtin?: string | null;
  status: string;
  message: string;
  canQuarantine: boolean;
  canMoveHere: boolean;
  product?: ZoneCountProductInfo | null;
  lot?: string | null;
  expiration?: string | null;
  recordedZone?: { id: string; name: string } | null;
  salesOrderNumber?: string | null;
};

// ───────────────────────────── the count ─────────────────────────────

/** A serialized scan the sheet does not list ("not on record in this zone"). */
export type FoundUnit = {
  /** serial|middle-12 — one entry per physical unit. */
  key: string;
  serialNumber: string;
  gtin: string | null;
  /** Scan text / typed code, so the server can name a product without a GTIN. */
  code: string | null;
  lot: string | null;
  /** YYYY-MM-DD as scanned. */
  expiration: string | null;
  /** The server's answer; null while it is being asked (or after a failed ask). */
  lookup: ZoneCountUnitLookup | null;
  lookupFailed?: boolean;
};

/** Bulk stock on the shelf of a lot the sheet does not list. */
export type FoundStock = {
  id: string;
  product: ZoneCountProductInfo;
  lot: string | null;
  /** YYYY-MM-DD */
  expiration: string | null;
  quantity: number;
};

export type CountState = {
  /** Listed serialized units scanned, in scan order. */
  scanned: string[];
  /** Entered counts per lot key. Absent = not counted yet (0 is a count). */
  bulk: Record<string, number>;
  found: FoundUnit[];
  foundStock: FoundStock[];
};

export const emptyCount = (): CountState => ({ scanned: [], bulk: {}, found: [], foundStock: [] });

export type UnitRef = { product: ZoneCountProduct; lot: ZoneCountLot; unit: ZoneCountUnit };

export type SheetIndex = {
  units: Map<string, UnitRef>;
  /** Listed units by serial number (a serial is unique only within its product). */
  bySerial: Map<string, UnitRef[]>;
  lots: Map<string, { product: ZoneCountProduct; lot: ZoneCountLot }>;
};

export const indexSheet = (sheet: ZoneCountSheet): SheetIndex => {
  const units = new Map<string, UnitRef>();
  const bySerial = new Map<string, UnitRef[]>();
  const lots = new Map<string, { product: ZoneCountProduct; lot: ZoneCountLot }>();
  for (const product of sheet.products) {
    for (const lot of product.lots) {
      lots.set(lot.key, { product, lot });
      for (const unit of lot.units) {
        const ref = { product, lot, unit };
        units.set(unit.serializedItemId, ref);
        const list = bySerial.get(unit.serialNumber) || [];
        list.push(ref);
        bySerial.set(unit.serialNumber, list);
      }
    }
  }
  return { units, bySerial, lots };
};

export const unitsLabel = (count: number) => `${count} ${count === 1 ? 'unit' : 'units'}`;

const digits = (value: unknown) => String(value ?? '').replace(/\D/g, '');

const middleOfGtin = (gtin: string | null | undefined) =>
  gtinMiddle12(gtin) ?? gtinMiddle12(retailCodeGtin14(gtin));

export const foundUnitKey = (serialNumber: string, middle12: string | null | undefined) =>
  `${serialNumber}|${middle12 || ''}`;

// ───────────────────────────── which record a scan names ─────────────────────────────

export type CountScanMatch =
  /** A listed serialized unit. */
  | { kind: 'unit'; ref: UnitRef }
  /** A sealed case / pack / pallet in the zone: counts the listed units inside. */
  | { kind: 'container'; container: ZoneCountContainer }
  /** A serial the sheet does not list. */
  | {
      kind: 'foundUnit';
      serialNumber: string;
      gtin: string | null;
      middle12: string | null;
      code: string | null;
      lot: string | null;
      expiration: string | null;
    }
  /** A pallet label the zone has no record of. */
  | { kind: 'unknownPallet'; code: string }
  /** A product barcode with no serial, of a product counted by number here. */
  | { kind: 'bulk'; product: ZoneCountProduct; lots: ZoneCountLot[] }
  /** A product counted by serial here, scanned from a barcode with no serial. */
  | { kind: 'needsSerial'; product: ZoneCountProduct }
  /** A product code the zone has no record of. */
  | { kind: 'unknownProduct'; code: string; gtin: string | null; lot: string | null; expiration: string | null }
  | { kind: 'notProduct'; text: string };

/** The 17 meaningful digits of an SSCC however it was read (AI 00 + 18, 18, or 17 digits). */
const ssccCore = (value: string | null | undefined): string | null => {
  const d = digits(value);
  if (d.length === 20 && d.startsWith('00')) return d.slice(2, 19);
  if (d.length === 18) return d.slice(0, 17);
  if (d.length === 17) return d;
  return null;
};

const productMatchesMiddle = (product: ZoneCountProductInfo, middle12: string | null) =>
  !!middle12 && (product.matchKeys || []).includes(middle12);

const productMatchesTyped = (product: ZoneCountProductInfo, code: string) => {
  const typed = digits(code);
  return (
    !!typed &&
    (digits(product.ndc) === typed ||
      digits(product.tenDigitNdc) === typed ||
      digits(product.upc) === typed)
  );
};

const bulkLotsOf = (product: ZoneCountProduct, lot: string | null | undefined) => {
  const lots = product.lots.filter((l) => l.bulkQuantity > 0);
  if (!lot || lots.length < 2) return lots;
  const narrowed = lots.filter((l) => (l.lot || '') === lot);
  return narrowed.length ? narrowed : lots;
};

type CountScanLike = ProductScanLike & { kind?: string | null; code?: string | null };

/**
 * The record a camera / handheld scan (or a manual entry shaped like one) names in this zone. A
 * serial is matched against the listed units of the scanned product — never another product's unit
 * that happens to share it — and against the sealed containers; a serial matching neither is a unit
 * found on the shelf. A barcode with no serial names a product: counted by number when it holds bulk
 * stock here, or a nudge to scan the 2D code when it is counted by serial.
 */
export const matchCountScan = (
  sheet: ZoneCountSheet,
  index: SheetIndex,
  scan: CountScanLike
): CountScanMatch => {
  const text = (scan?.text || scan?.code || '').trim();
  if (!scan || (!scan.isManualEntry && !looksLikeProductScan(scan))) {
    return { kind: 'notProduct', text };
  }
  // A bare retail code's GS1 mis-parse "serial" is dropped (see retailCode.ts); a typed one is not.
  const retail = !scan.isManualEntry && isRetailCodeScan(scan);
  const serial = retail ? null : scan.serialNumber?.trim() || null;
  const lot = retail ? null : scan.lot?.trim() || null;
  const expiration = retail ? null : scan.expiration || null;
  const middle12 = scanGtinMiddle12(scan);
  const typedCode = scan.isManualEntry ? (scan.code || scan.text || '').trim() || null : null;

  // A pallet label: AI 00, no GTIN.
  if (!scan.gtin && (scan.sscc || scan.kind === 'sscc')) {
    const core = ssccCore(scan.sscc) ?? ssccCore(serial);
    const pallet = sheet.containers.find(
      (c) =>
        c.type === 'SSCC' &&
        (c.serialNumber === serial || (!!core && ssccCore(c.serialNumber) === core))
    );
    if (pallet) return { kind: 'container', container: pallet };
    return { kind: 'unknownPallet', code: scan.sscc || serial || text };
  }

  if (serial) {
    const candidates = getUniqueBySerialCaseInsensitive(index.bySerial, serial) || [];
    const hits = middle12
      ? candidates.filter(
          (ref) =>
            middleOfGtin(ref.unit.gtin) === middle12 || productMatchesMiddle(ref.product, middle12)
        )
      : typedCode && digits(typedCode)
      ? candidates.filter((ref) => productMatchesTyped(ref.product, typedCode))
      : candidates;
    if (hits.length) return { kind: 'unit', ref: hits[0] };
    const container = matchUniqueSerialCaseInsensitive(
      sheet.containers.filter(
        (c) =>
          c.type !== 'SSCC' &&
          (!middle12 || !c.gtin || middleOfGtin(c.gtin) === middle12)
      ),
      serial,
      (candidate) => candidate.serialNumber
    );
    if (container) return { kind: 'container', container };
    return {
      kind: 'foundUnit',
      serialNumber: serial,
      gtin: scan.gtin ? retailCodeGtin14(digits(scan.gtin)) || scan.gtin : null,
      middle12,
      code: typedCode || text || null,
      lot,
      expiration
    };
  }

  const products = sheet.products.filter(
    (p) =>
      productMatchesMiddle(p, middle12) || (!!typedCode && productMatchesTyped(p, typedCode))
  );
  if (!products.length) {
    const code = typedCode || text;
    if (!middle12 && !digits(code)) return { kind: 'notProduct', text };
    return { kind: 'unknownProduct', code, gtin: scan.gtin || null, lot, expiration };
  }
  const product = products[0];
  const lots = bulkLotsOf(product, lot);
  if (!lots.length) return { kind: 'needsSerial', product };
  return { kind: 'bulk', product, lots };
};

// ───────────────────────────── counting ─────────────────────────────

export const isScanned = (state: CountState, unitId: string) => state.scanned.includes(unitId);

export const countUnits = (state: CountState, unitIds: string[]): CountState => {
  const seen = new Set(state.scanned);
  const added = unitIds.filter((id) => !seen.has(id));
  return added.length ? { ...state, scanned: [...state.scanned, ...added] } : state;
};

export const uncountUnits = (state: CountState, unitIds: string[]): CountState => {
  const drop = new Set(unitIds);
  return state.scanned.some((id) => drop.has(id))
    ? { ...state, scanned: state.scanned.filter((id) => !drop.has(id)) }
    : state;
};

/** An entered count, held to a whole number ≥ 0; null clears it back to "not counted". */
export const setBulkCount = (
  state: CountState,
  lotKey: string,
  value: number | null
): CountState => {
  const bulk = { ...state.bulk };
  if (value == null || !Number.isFinite(value)) delete bulk[lotKey];
  else bulk[lotKey] = Math.max(0, Math.trunc(value));
  return { ...state, bulk };
};

export const bumpBulkCount = (state: CountState, lotKey: string, by: number): CountState => {
  const current = state.bulk[lotKey];
  const next = (current ?? 0) + by;
  if (current == null && by < 0) return state;
  return setBulkCount(state, lotKey, Math.max(0, next));
};

export const hasFoundUnit = (state: CountState, key: string) =>
  state.found.some((f) => f.key === key);

export const addFoundUnit = (state: CountState, found: FoundUnit): CountState =>
  hasFoundUnit(state, found.key) ? state : { ...state, found: [...state.found, found] };

export const removeFoundUnit = (state: CountState, key: string): CountState => ({
  ...state,
  found: state.found.filter((f) => f.key !== key)
});

export const setFoundLookup = (
  state: CountState,
  key: string,
  lookup: ZoneCountUnitLookup | null,
  failed = false
): CountState => ({
  ...state,
  found: state.found.map((f) => (f.key === key ? { ...f, lookup, lookupFailed: failed } : f))
});

/** The listed lot of this product a found lot + expiration (YYYY-MM-DD) actually is, if any. */
export const listedLotFor = (
  sheet: ZoneCountSheet,
  productKey: string,
  lot: string | null,
  expiration: string | null
): ZoneCountLot | null => {
  const product = sheet.products.find((p) => p.key === productKey);
  if (!product) return null;
  return (
    product.lots.find(
      (l) =>
        l.bulkQuantity > 0 &&
        (l.lot || null) === (lot || null) &&
        (l.expiration && expiration
          ? sameCalendarDay(l.expiration, expiration)
          : !l.expiration && !expiration)
    ) || null
  );
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A stored instant and a YYYY-MM-DD are the same expiration within a day either way — stored
 * expirations are midnight in whatever timezone wrote them (the server's `sameExpirationDay` rule).
 */
export const sameCalendarDay = (instant: string, ymd: string) => {
  const stored = new Date(instant).getTime();
  const typed = Date.parse(`${ymd}T12:00:00.000Z`);
  if (Number.isNaN(stored) || Number.isNaN(typed)) return false;
  const day = (t: number) => Math.floor(t / DAY_MS);
  return Math.abs(day(stored) - day(typed)) <= 1;
};

export const upsertFoundStock = (state: CountState, stock: FoundStock): CountState => {
  const exists = state.foundStock.some((s) => s.id === stock.id);
  return {
    ...state,
    foundStock: exists
      ? state.foundStock.map((s) => (s.id === stock.id ? stock : s))
      : [...state.foundStock, stock]
  };
};

export const removeFoundStock = (state: CountState, id: string): CountState => ({
  ...state,
  foundStock: state.foundStock.filter((s) => s.id !== id)
});

/** YYYY-MM-DD of a stored instant (its UTC calendar day). */
export const isoDay = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

// ───────────────────────────── progress ─────────────────────────────

export type LotProgress = {
  /** Listed units scanned. */
  scannedUnits: number;
  /** The entered count, or null when not counted yet. */
  bulkCounted: number | null;
  /** What the lot counts so far (scanned + entered). */
  counted: number;
  /** Counted exactly what the records say. */
  done: boolean;
  /** Entered more than the records say. */
  over: number;
};

export const lotProgress = (lot: ZoneCountLot, state: CountState): LotProgress => {
  const scannedSet = new Set(state.scanned);
  const scannedUnits = lot.units.filter((u) => scannedSet.has(u.serializedItemId)).length;
  const bulkCounted = state.bulk[lot.key] ?? null;
  const counted = scannedUnits + (bulkCounted ?? 0);
  const bulkDone = lot.bulkQuantity === 0 ? bulkCounted == null || bulkCounted === 0 : bulkCounted === lot.bulkQuantity;
  return {
    scannedUnits,
    bulkCounted,
    counted,
    done: scannedUnits === lot.units.length && bulkDone,
    over: Math.max(0, (bulkCounted ?? 0) - lot.bulkQuantity)
  };
};

export const productProgress = (product: ZoneCountProduct, state: CountState) => {
  const lots = product.lots.map((lot) => lotProgress(lot, state));
  return {
    counted: lots.reduce((sum, l) => sum + l.counted, 0),
    done: lots.every((l) => l.done),
    over: lots.reduce((sum, l) => sum + l.over, 0)
  };
};

export type CountSummary = {
  expected: number;
  /** Everything counted: listed units, entered numbers, found units and found stock. */
  counted: number;
  /** Units the records list that are not counted yet. */
  toGo: number;
  /** Units counted that the records do not have here. */
  notOnRecord: number;
  /** Bulk lots with no count entered yet. */
  uncountedLots: number;
};

export const countSummary = (sheet: ZoneCountSheet, state: CountState): CountSummary => {
  let counted = 0;
  let toGo = 0;
  let over = 0;
  let uncountedLots = 0;
  for (const product of sheet.products) {
    for (const lot of product.lots) {
      const p = lotProgress(lot, state);
      counted += p.counted;
      toGo += Math.max(0, lot.units.length - p.scannedUnits);
      toGo += Math.max(0, lot.bulkQuantity - (p.bulkCounted ?? 0));
      over += p.over;
      if (lot.bulkQuantity > 0 && p.bulkCounted == null) uncountedLots += 1;
    }
  }
  const foundStock = state.foundStock.reduce((sum, s) => sum + s.quantity, 0);
  counted += state.found.length + foundStock;
  return {
    expected: sheet.expectedTotal,
    counted,
    toGo,
    notOnRecord: over + state.found.length + foundStock,
    uncountedLots
  };
};

export const hasCounted = (state: CountState) =>
  state.scanned.length > 0 ||
  Object.keys(state.bulk).length > 0 ||
  state.found.length > 0 ||
  state.foundStock.length > 0;

// ───────────────────────────── the review ─────────────────────────────

export type MissingLot = { product: ZoneCountProduct; lot: ZoneCountLot; units: ZoneCountUnit[] };
export type BulkVariance = {
  /** Lot key, or `found:<id>` for found stock. */
  key: string;
  product: ZoneCountProductInfo;
  lot: ZoneCountLot | null;
  lotNumber: string | null;
  /** The listed lot's instant (ISO), or found stock's YYYY-MM-DD. */
  expiration: string | null;
  expected: number;
  counted: number;
  /** counted − expected: < 0 short, > 0 over. */
  variance: number;
  drift: boolean;
};

export type ZoneCountReview = {
  missing: MissingLot[];
  missingCount: number;
  found: FoundUnit[];
  short: BulkVariance[];
  over: BulkVariance[];
  notCounted: { product: ZoneCountProduct; lot: ZoneCountLot }[];
  /** Nothing missing, nothing found, every bulk lot counted and matching. */
  matches: boolean;
  /** Controlled (CI–CV) products with something missing or short. */
  controlledShort: string[];
};

export const reviewCount = (sheet: ZoneCountSheet, state: CountState): ZoneCountReview => {
  const scannedSet = new Set(state.scanned);
  const missing: MissingLot[] = [];
  const short: BulkVariance[] = [];
  const over: BulkVariance[] = [];
  const notCounted: ZoneCountReview['notCounted'] = [];
  const controlledShort = new Set<string>();
  for (const product of sheet.products) {
    for (const lot of product.lots) {
      const units = lot.units.filter((u) => !scannedSet.has(u.serializedItemId));
      if (units.length) {
        missing.push({ product, lot, units });
        if (product.controlled) controlledShort.add(product.productName);
      }
      if (lot.bulkQuantity <= 0 && state.bulk[lot.key] == null) continue;
      const counted = state.bulk[lot.key];
      if (counted == null) {
        notCounted.push({ product, lot });
        continue;
      }
      const variance = counted - lot.bulkQuantity;
      if (!variance) continue;
      const entry: BulkVariance = {
        key: lot.key,
        product,
        lot,
        lotNumber: lot.lot ?? null,
        expiration: lot.expiration ?? null,
        expected: lot.bulkQuantity,
        counted,
        variance,
        drift: lot.drift
      };
      if (variance < 0) {
        short.push(entry);
        if (product.controlled) controlledShort.add(product.productName);
      } else over.push(entry);
    }
  }
  for (const stock of state.foundStock) {
    if (stock.quantity <= 0) continue;
    over.push({
      key: `found:${stock.id}`,
      product: stock.product,
      lot: null,
      lotNumber: stock.lot,
      expiration: stock.expiration,
      expected: 0,
      counted: stock.quantity,
      variance: stock.quantity,
      drift: false
    });
  }
  const missingCount = missing.reduce((sum, m) => sum + m.units.length, 0);
  return {
    missing,
    missingCount,
    found: state.found,
    short,
    over,
    notCounted,
    matches:
      !missingCount && !state.found.length && !short.length && !over.length && !notCounted.length,
    controlledShort: [...controlledShort]
  };
};

/** What the operator chose on the review. */
export type ReviewSelection = {
  /** Missing unit ids to quarantine. */
  missing: string[];
  /** Per found unit key. */
  found: Record<string, 'QUARANTINE' | 'MOVE_HERE' | 'SKIP'>;
  /** Units to quarantine per variance key (short or over). */
  bulk: Record<string, number>;
};

const missingActionable = (m: MissingLot) => !m.lot.drift;

/** The default choice: everything the count can act on. */
export const defaultSelection = (review: ZoneCountReview): ReviewSelection => ({
  missing: review.missing
    .filter(missingActionable)
    .flatMap((m) => m.units.map((u) => u.serializedItemId)),
  found: Object.fromEntries(
    review.found.map((f) => [f.key, f.lookup?.canQuarantine ? 'QUARANTINE' : 'SKIP'])
  ) as ReviewSelection['found'],
  bulk: Object.fromEntries(
    [...review.short, ...review.over]
      .filter((v) => !v.drift)
      .map((v) => [v.key, Math.abs(v.variance)])
  )
});

export type SelectionTotals = {
  quarantine: number;
  moveHere: number;
  changes: number;
};

export const selectionTotals = (
  review: ZoneCountReview,
  selection: ReviewSelection
): SelectionTotals => {
  const foundChoices = review.found.map((f) => selection.found[f.key]);
  const bulk = [...review.short, ...review.over].reduce(
    (sum, v) => sum + (v.drift ? 0 : Math.min(selection.bulk[v.key] ?? 0, Math.abs(v.variance))),
    0
  );
  const foundQuarantine = foundChoices.filter((c) => c === 'QUARANTINE').length;
  const moveHere = foundChoices.filter((c) => c === 'MOVE_HERE').length;
  const quarantine = selection.missing.length + foundQuarantine + bulk;
  return { quarantine, moveHere, changes: quarantine + moveHere };
};

/** The zoneCountApply variables for what was chosen (no quarantine zone yet). */
export const applyInput = (review: ZoneCountReview, selection: ReviewSelection) => {
  const chosenMissing = new Set(selection.missing);
  const missingUnitIds = review.missing
    .filter(missingActionable)
    .flatMap((m) => m.units.map((u) => u.serializedItemId))
    .filter((id) => chosenMissing.has(id));
  const foundUnits = review.found
    .filter((f) => selection.found[f.key] === 'QUARANTINE' || selection.found[f.key] === 'MOVE_HERE')
    .map((f) => ({
      serialNumber: f.serialNumber,
      gtin: f.gtin,
      code: f.code,
      lot: f.lot,
      expiration: f.expiration,
      action: selection.found[f.key]
    }));
  const quantityFor = (v: BulkVariance) =>
    v.drift ? 0 : Math.min(Math.max(0, Math.trunc(selection.bulk[v.key] ?? 0)), Math.abs(v.variance));
  const shortages = review.short
    .filter((v) => quantityFor(v) > 0 && v.lot?.zoneInventoryBalanceId)
    .map((v) => ({ zoneInventoryBalanceId: v.lot!.zoneInventoryBalanceId!, quantity: quantityFor(v) }));
  const overages = review.over
    .filter((v) => quantityFor(v) > 0)
    .map((v) => ({
      fdaProductId: v.product.fdaProductId || null,
      upcProductId: v.product.fdaProductId ? null : v.product.upcProductId || null,
      lot: v.lotNumber,
      expiration: v.expiration,
      quantity: quantityFor(v)
    }));
  return { missingUnitIds, foundUnits, shortages, overages };
};

/** Whether the chosen changes need a quarantine zone (everything but "move here" does). */
export const needsQuarantineZone = (input: ReturnType<typeof applyInput>) =>
  input.missingUnitIds.length > 0 ||
  input.shortages.length > 0 ||
  input.overages.length > 0 ||
  input.foundUnits.some((f) => f.action === 'QUARANTINE');

// ───────────────────────────── the draft (survives a reload) ─────────────────────────────

const DRAFT_PREFIX = 'rxerp.zoneCount.v1';
/** A count left untouched this long is not offered back. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type ZoneCountDraft = {
  v: 1;
  zoneId: string;
  zoneName: string;
  startedAt: number;
  updatedAt: number;
  /** Scanned units with their codes, so a resumed count re-matches them against fresh records. */
  scanned: { id: string; serialNumber: string; gtin: string | null }[];
  bulk: Record<string, number>;
  found: Omit<FoundUnit, 'lookup' | 'lookupFailed'>[];
  foundStock: FoundStock[];
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

export const draftKey = (companyId: string, zoneId: string) => `${DRAFT_PREFIX}:${companyId}:${zoneId}`;

export const toDraft = (
  sheet: ZoneCountSheet,
  state: CountState,
  startedAt: number,
  now: number
): ZoneCountDraft => {
  const index = indexSheet(sheet);
  return {
    v: 1,
    zoneId: sheet.zone.id,
    zoneName: sheet.zone.name,
    startedAt,
    updatedAt: now,
    scanned: state.scanned.map((id) => {
      const ref = index.units.get(id);
      return { id, serialNumber: ref?.unit.serialNumber || '', gtin: ref?.unit.gtin || null };
    }),
    bulk: state.bulk,
    found: state.found.map(({ lookup: _lookup, lookupFailed: _failed, ...rest }) => rest),
    foundStock: state.foundStock
  };
};

const parseDraft = (raw: string | null, now: number): ZoneCountDraft | null => {
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw);
    if (draft?.v !== 1 || !draft.zoneId || !Array.isArray(draft.scanned)) return null;
    if (now - Number(draft.updatedAt || 0) > DRAFT_MAX_AGE_MS) return null;
    return draft as ZoneCountDraft;
  } catch {
    return null;
  }
};

export const saveDraft = (storage: StorageLike, companyId: string, draft: ZoneCountDraft) => {
  try {
    storage.setItem(draftKey(companyId, draft.zoneId), JSON.stringify(draft));
  } catch {
    // Storage full or unavailable: the count carries on, it just will not survive a reload.
  }
};

export const loadDraft = (
  storage: StorageLike,
  companyId: string,
  zoneId: string,
  now: number
): ZoneCountDraft | null => {
  try {
    return parseDraft(storage.getItem(draftKey(companyId, zoneId)), now);
  } catch {
    return null;
  }
};

export const clearDraft = (storage: StorageLike, companyId: string, zoneId: string) => {
  try {
    storage.removeItem(draftKey(companyId, zoneId));
  } catch {
    // Nothing to clear.
  }
};

/** Every count in progress for this company, newest first. */
export const listDrafts = (storage: StorageLike, companyId: string, now: number): ZoneCountDraft[] => {
  const prefix = `${DRAFT_PREFIX}:${companyId}:`;
  const drafts: ZoneCountDraft[] = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const draft = parseDraft(storage.getItem(key), now);
      if (draft) drafts.push(draft);
    }
  } catch {
    return [];
  }
  return drafts.sort((a, b) => b.updatedAt - a.updatedAt);
};

export const draftCounted = (draft: ZoneCountDraft) =>
  draft.scanned.length +
  Object.values(draft.bulk || {}).reduce((sum, n) => sum + (Number(n) || 0), 0) +
  (draft.found || []).length +
  (draft.foundStock || []).reduce((sum, s) => sum + (Number(s.quantity) || 0), 0);

/**
 * A saved count laid over fresh records. A scanned unit still listed (by id, else by serial and
 * product) stays counted; one that has left the zone's records since becomes a unit found on the
 * shelf, to be looked up again — and a found unit the records now list here (recorded here since)
 * is simply counted. Entered counts carry over for lots that are still listed.
 */
export const restoreDraft = (sheet: ZoneCountSheet, draft: ZoneCountDraft): CountState => {
  const index = indexSheet(sheet);
  const scanned: string[] = [];
  const found: FoundUnit[] = [];
  const listedBySerial = (serialNumber: string, gtin: string | null | undefined) => {
    const middle = middleOfGtin(gtin);
    return (getUniqueBySerialCaseInsensitive(index.bySerial, serialNumber) || []).find(
      (r) => !middle || middleOfGtin(r.unit.gtin) === middle
    );
  };
  for (const entry of draft.scanned) {
    if (index.units.has(entry.id)) {
      scanned.push(entry.id);
      continue;
    }
    const middle = middleOfGtin(entry.gtin);
    const ref = listedBySerial(entry.serialNumber, entry.gtin);
    if (ref) scanned.push(ref.unit.serializedItemId);
    else if (entry.serialNumber) {
      found.push({
        key: foundUnitKey(entry.serialNumber, middle),
        serialNumber: entry.serialNumber,
        gtin: entry.gtin,
        code: null,
        lot: null,
        expiration: null,
        lookup: null
      });
    }
  }
  for (const f of draft.found || []) {
    const ref = listedBySerial(f.serialNumber, f.gtin);
    if (ref) scanned.push(ref.unit.serializedItemId);
    else if (!found.some((x) => x.key === f.key)) found.push({ ...f, lookup: null });
  }
  const bulk: Record<string, number> = {};
  for (const [key, value] of Object.entries(draft.bulk || {})) {
    if (index.lots.has(key) && Number.isFinite(Number(value))) bulk[key] = Math.max(0, Math.trunc(Number(value)));
  }
  return {
    scanned: [...new Set(scanned)],
    bulk,
    found,
    foundStock: draft.foundStock || []
  };
};
